<template>
  <div
    :dir="isRtl === true ? 'rtl' : 'ltr'"
    ref="selectorRootRef"
    class="relative w-full entity-icon-selector-dropdown-container"
  >
    <button
      type="button"
      class="btn btn-outline w-full justify-start font-normal text-left py-2 px-3 min-h-0"
      :class="{ 'ring-2 ring-primary': isOpen === true }"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen === true"
    >
      <div v-if="selectedIconObject !== null" class="flex items-center w-full entity-icon-selector-trigger-row">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0">
          <Icon :icon="selectedIconObject.id" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-grow overflow-hidden entity-icon-selector-trigger-copy" :class="isRtl === true ? 'text-right' : 'text-left'">
          <span class="block text-sm font-medium truncate">{{ selectedIconObject.displayName }}</span>
          <span class="block text-xs text-base-content opacity-70 truncate">{{ selectedIconObject.id }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0" />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-sm text-base-content opacity-70">{{ t('entityIconSelector.selectPlaceholder', 'Select icon') }}</span>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen === true"
        ref="dropdownListRef"
        class="fixed z-[10050] bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 entity-icon-options-list flex flex-col"
        :style="dropdownStyle"
        role="listbox"
      >
        <div class="p-2 border-b border-base-300 dark:border-gray-700">
          <input
            v-model="filterQuery"
            type="text"
            class="input input-bordered input-sm w-full"
            :placeholder="t('entityIconSelector.filterPlaceholder', 'Filter icons...')"
            @click.stop
          />
        </div>
        <div class="py-1 entity-icon-options-grid-wrap overflow-y-auto">
          <div
            v-for="iconOption in filteredIconOptions"
            :key="iconOption.id"
            class="entity-icon-option card-as-row rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{
              'bg-primary text-primary-content': iconOption.id === selectedIcon,
              'hover:bg-base-200 dark:hover:bg-gray-700': iconOption.id !== selectedIcon
            }"
            @click="selectIconAndClose(iconOption.id)"
          >
            <div class="p-2 flex flex-col items-center">
              <span class="inline-flex justify-center items-center w-9 h-9 rounded-lg bg-base-300 dark:bg-gray-600">
                <Icon :icon="iconOption.id" class="w-5 h-5 text-base-content" />
              </span>
              <span class="text-xs mt-1 text-center truncate w-full">{{ iconOption.displayName }}</span>
            </div>
          </div>
          <div v-if="filteredIconOptions.length === 0" class="col-span-full px-3 py-4 text-sm text-center text-base-content opacity-70">
            {{ t('entityIconSelector.noMatches', 'No matching icons.') }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { entityIconPresets, resolveEntityIconValue } from '@/shared/entityCatalog.js';

const props = defineProps({
  selectedIcon: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:selectedIcon']);

const isOpen = ref(false);
const filterQuery = ref('');
const selectorRootRef = ref(null);
const dropdownListRef = ref(null);
const dropdownStyle = ref({});
const { t, currentLanguage } = useI18n();

const isRtl = computed(() => {
  if (currentLanguage.value === 'he') {
    return true;
  }
  if (currentLanguage.value === 'ar') {
    return true;
  }
  return false;
});

const iconOptions = computed(() => {
  const options = [];
  for (let index = 0; index < entityIconPresets.length; index = index + 1) {
    const preset = entityIconPresets[index];
    const labelKey = typeof preset.labelKey === 'string' ? preset.labelKey : preset.id;
    options.push({
      id: preset.id,
      displayName: t(`entityIconSelector.icons.${labelKey}`, labelKey)
    });
  }
  return options;
});

const filteredIconOptions = computed(() => {
  const query = typeof filterQuery.value === 'string' ? filterQuery.value.trim().toLowerCase() : '';
  const options = iconOptions.value;
  if (query === '') {
    return options;
  }
  const filtered = [];
  for (let index = 0; index < options.length; index = index + 1) {
    const option = options[index];
    const haystack = `${option.id} ${option.displayName}`.toLowerCase();
    if (haystack.indexOf(query) > -1) {
      filtered.push(option);
    }
  }
  return filtered;
});

const selectedIcon = computed(() => resolveEntityIconValue(props.selectedIcon));

const selectedIconObject = computed(() => {
  if (selectedIcon.value === '') {
    return null;
  }
  for (let index = 0; index < iconOptions.value.length; index = index + 1) {
    if (iconOptions.value[index].id === selectedIcon.value) {
      return iconOptions.value[index];
    }
  }
  return {
    id: selectedIcon.value,
    displayName: selectedIcon.value
  };
});

function positionDropdown() {
  if (selectorRootRef.value === null || selectorRootRef.value === undefined) {
    return;
  }
  if (dropdownListRef.value === null || dropdownListRef.value === undefined) {
    return;
  }
  const triggerRect = selectorRootRef.value.getBoundingClientRect();
  const dropdownMaxHeight = 320;
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  let topPosition = triggerRect.bottom + 4;
  if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow && spaceAbove > dropdownMaxHeight) {
    topPosition = triggerRect.top - dropdownListRef.value.offsetHeight - 4;
    if (topPosition < 4) {
      topPosition = 4;
    }
  }
  dropdownStyle.value = {
    position: 'fixed',
    width: `${triggerRect.width}px`,
    left: `${triggerRect.left}px`,
    top: `${topPosition}px`,
    maxHeight: `${dropdownMaxHeight}px`
  };
}

async function toggleDropdown() {
  if (isOpen.value === true) {
    isOpen.value = false;
    return;
  }
  isOpen.value = true;
  await nextTick();
  positionDropdown();
  await nextTick();
  positionDropdown();
}

function closeDropdown() {
  isOpen.value = false;
  filterQuery.value = '';
}

function selectIconAndClose(iconId) {
  if (props.selectedIcon !== iconId) {
    emit('update:selectedIcon', iconId);
  }
  closeDropdown();
}

function handleClickOutside(event) {
  if (isOpen.value !== true) {
    return;
  }
  const rootElement = selectorRootRef.value;
  const menuElement = dropdownListRef.value;
  if (rootElement !== null && rootElement !== undefined && rootElement.contains(event.target) === true) {
    return;
  }
  if (menuElement !== null && menuElement !== undefined && menuElement.contains(event.target) === true) {
    return;
  }
  closeDropdown();
}

watch(isOpen, (open) => {
  if (open === true) {
    window.addEventListener('resize', positionDropdown);
    window.addEventListener('scroll', positionDropdown, true);
  } else {
    window.removeEventListener('resize', positionDropdown);
    window.removeEventListener('scroll', positionDropdown, true);
  }
});

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('resize', positionDropdown);
  window.removeEventListener('scroll', positionDropdown, true);
});
</script>

<style scoped>
.entity-icon-options-list {
  max-height: 320px;
}

.entity-icon-options-grid-wrap {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0.25rem;
  margin: -0.25rem;
  max-height: 260px;
}

.entity-icon-option {
  margin: 0.25rem;
}

.entity-icon-selector-trigger-row > * {
  margin-right: 0.5rem;
}

.entity-icon-selector-trigger-copy {
  margin-right: 0.5rem;
}
</style>
